import React, { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../utils/api";
import { useModal } from "../../context/ModalContext";
import {
  Card, CardBody, PageHeader, Badge, Button, Input, Spinner, EmptyState,
} from "../../components/ui";
import {
  FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaHistory, FaArrowLeft, FaRobot,
} from "react-icons/fa";
import AutomationFlowCanvas from "../../components/automations/AutomationFlowCanvas";

const EVENT_LABELS = {
  "user.signup": "User Signup",
  "user.trial_started": "Trial Started",
  "user.trial_expiring": "Trial Expiring",
  "user.subscription_expired": "Subscription Expired",
  "user.plan_upgraded": "Plan Upgraded",
  "user.plan_downgraded": "Plan Downgraded",
  "session.disconnected_persistent": "WhatsApp Disconnected",
  "user.inactive": "User Inactive",
  "user.low_message_quota": "Low Message Quota",
  "user.payment_failed": "Payment Failed",
};

// ─── LOGS PANEL ─────────────────────────────────────────

function LogsPanel({ automationId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet(`/api/admin/automations/logs?automationId=${automationId}`)
      .then((r) => { if (r.success) setLogs(r.data); })
      .finally(() => setLoading(false));
  }, [automationId]);

  if (loading) return <Spinner />;
  if (!logs.length) return <p className="text-sm text-ink-muted py-4 text-center">No execution logs yet.</p>;

  return (
    <div className="max-h-80 overflow-y-auto space-y-1">
      {logs.map((log) => (
        <div key={log.id} className="flex items-center gap-3 text-xs py-1.5 px-2 rounded hover:bg-surface-bg">
          <Badge variant={log.status === "sent" ? "success" : log.status === "failed" ? "danger" : "neutral"}>
            {log.status}
          </Badge>
          <span className="text-ink-muted">{log.actionType}</span>
          <span className="text-ink-secondary flex-1 truncate">{log.detail}</span>
          <span className="text-ink-muted">{new Date(log.createdAt).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

// ─── MAIN COMPONENT ─────────────────────────────────────

export default function ManageAutomations() {
  const modal = useModal();
  const [automations, setAutomations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Views: "list" | "editor" | "logs"
  const [view, setView] = useState("list");
  const [editingAuto, setEditingAuto] = useState(null); // null = new
  const [autoName, setAutoName] = useState("");
  const [viewLogsId, setViewLogsId] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [autoRes, sessRes] = await Promise.all([
      apiGet("/api/admin/automations"),
      apiGet("/api/sessions"),
    ]);
    if (autoRes.success) setAutomations(autoRes.data);
    if (sessRes.success) setSessions(Array.isArray(sessRes.data) ? sessRes.data : []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── HANDLERS ──────────────────────────────

  const handleNew = () => {
    setEditingAuto(null);
    setAutoName("");
    setView("editor");
  };

  const handleEdit = async (auto) => {
    // Load full automation with actions (nodes/edges)
    const res = await apiGet(`/api/admin/automations/${auto.id}`);
    if (res.success) {
      setEditingAuto(res.data);
      setAutoName(res.data.name);
      setView("editor");
    }
  };

  const handleSaveFlow = async (nodes, edges) => {
    if (!autoName.trim()) {
      showToast("Please enter an automation name", "error");
      return;
    }

    // Extract event from trigger node
    const triggerNode = nodes.find((n) => n.type === "eventTrigger");
    const event = triggerNode?.data?.event;
    if (!event) {
      showToast("Please select an event in the trigger node", "error");
      return;
    }

    const payload = {
      name: autoName,
      event,
      actions: { nodes, edges },
      enabled: editingAuto?.enabled !== false,
    };

    let res;
    if (editingAuto?.id) {
      res = await apiPut(`/api/admin/automations/${editingAuto.id}`, payload);
    } else {
      res = await apiPost("/api/admin/automations", payload);
    }

    if (res.success) {
      showToast(editingAuto?.id ? "Automation updated" : "Automation created");
      setView("list");
      fetchData();
    } else {
      showToast(res.message || "Failed to save", "error");
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await modal.confirm("Delete this automation and all its logs?");
    if (!confirmed) return;
    const res = await apiDelete(`/api/admin/automations/${id}`);
    if (res.success) {
      showToast("Automation deleted");
      fetchData();
    }
  };

  const handleToggle = async (auto) => {
    const res = await apiPut(`/api/admin/automations/${auto.id}`, { enabled: !auto.enabled });
    if (res.success) fetchData();
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  // ─── TOAST ────────────────────────────────
  const ToastBar = toast && (
    <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg text-sm shadow-lg ${toast.type === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"}`}>
      {toast.message}
    </div>
  );

  // ─── LOGS VIEW ────────────────────────────
  if (view === "logs" && viewLogsId) {
    const auto = automations.find((a) => a.id === viewLogsId);
    return (
      <div className="max-w-4xl mx-auto">
        {ToastBar}
        <button onClick={() => { setView("list"); setViewLogsId(null); }} className="flex items-center gap-1.5 text-sm text-ink-secondary hover:text-ink-primary mb-4">
          <FaArrowLeft size={12} /> Back to Automations
        </button>
        <PageHeader title={`Logs: ${auto?.name || "Automation"}`} />
        <Card>
          <CardBody>
            <LogsPanel automationId={viewLogsId} />
          </CardBody>
        </Card>
      </div>
    );
  }

  // ─── FLOW EDITOR VIEW ─────────────────────
  if (view === "editor") {
    const actions = editingAuto?.actions;
    const hasGraph = actions && actions.nodes && actions.edges;

    return (
      <div className="flex flex-col h-[calc(100vh-64px)]">
        {ToastBar}
        {/* Header bar */}
        <div className="flex items-center gap-3 px-4 py-2 border-b border-surface-border bg-surface-card flex-shrink-0">
          <button onClick={() => setView("list")} className="text-ink-secondary hover:text-ink-primary">
            <FaArrowLeft size={14} />
          </button>
          <Input
            value={autoName}
            onChange={(e) => setAutoName(e.target.value)}
            placeholder="Automation name..."
            className="max-w-xs text-sm font-medium"
          />
          <span className="text-xs text-ink-muted">
            {editingAuto ? "Editing" : "New automation"}
          </span>
        </div>

        {/* Flow Canvas */}
        <div className="flex-1 min-h-0">
          <AutomationFlowCanvas
            initialNodes={hasGraph ? actions.nodes : undefined}
            initialEdges={hasGraph ? actions.edges : undefined}
            onSave={handleSaveFlow}
            sessions={sessions}
          />
        </div>
      </div>
    );
  }

  // ─── LIST VIEW ────────────────────────────
  return (
    <div className="max-w-4xl mx-auto">
      {ToastBar}
      <PageHeader title="Automations" subtitle="Visual flow builder for automated email, WhatsApp, and webhook actions">
        <Button variant="primary" onClick={handleNew}>
          <FaPlus size={12} className="mr-1.5" /> New Automation
        </Button>
      </PageHeader>

      {automations.length === 0 ? (
        <EmptyState
          icon={FaRobot}
          title="No automations yet"
          description='Click "New Automation" to create a visual flow that triggers on user events.'
        />
      ) : (
        <div className="space-y-3">
          {automations.map((auto) => {
            const actions = auto.actions;
            const isGraph = actions && actions.nodes;
            const nodeCount = isGraph ? actions.nodes.length : (Array.isArray(actions) ? actions.length : 0);

            return (
              <Card key={auto.id}>
                <CardBody className="flex items-center gap-4">
                  <button onClick={() => handleToggle(auto)} className="flex-shrink-0" title={auto.enabled ? "Disable" : "Enable"}>
                    {auto.enabled ? (
                      <FaToggleOn size={24} className="text-green-500" />
                    ) : (
                      <FaToggleOff size={24} className="text-ink-muted" />
                    )}
                  </button>

                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleEdit(auto)}>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-ink-primary">{auto.name}</h3>
                      <Badge variant={auto.enabled ? "success" : "neutral"}>
                        {auto.enabled ? "Active" : "Disabled"}
                      </Badge>
                      {isGraph && (
                        <Badge variant="brand">Flow</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-ink-muted">
                      <span>Trigger: <strong>{EVENT_LABELS[auto.event] || auto.event}</strong></span>
                      <span>{nodeCount} node(s)</span>
                      <span>{auto._count?.logs || 0} run(s)</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button onClick={() => { setViewLogsId(auto.id); setView("logs"); }} className="p-2 rounded-md hover:bg-surface-bg text-ink-muted hover:text-ink-primary" title="Logs">
                      <FaHistory size={14} />
                    </button>
                    <button onClick={() => handleEdit(auto)} className="p-2 rounded-md hover:bg-surface-bg text-ink-muted hover:text-ink-primary" title="Edit">
                      <FaEdit size={14} />
                    </button>
                    <button onClick={() => handleDelete(auto.id)} className="p-2 rounded-md hover:bg-surface-bg text-ink-muted hover:text-red-500" title="Delete">
                      <FaTrash size={14} />
                    </button>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
